import { useState, useEffect, useRef, useMemo } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
	ClassicEditor,
	Alignment,
	Autoformat,
	AutoImage,
	AutoLink,
	Autosave,
	BlockQuote,
	Bold,
	Bookmark,
	Code,
	CodeBlock,
	Emoji,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	FullPage,
	Fullscreen,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	HtmlComment,
	HtmlEmbed,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Mention,
	Paragraph,
	PasteFromOffice,
	PlainTableOutput,
	RemoveFormat,
	ShowBlocks,
	SimpleUploadAdapter,
	SourceEditing,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	Strikethrough,
	Style,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";
import "./customCkEditorStyles.css";
import uploadFileService from "services/UploadFileService";

const LICENSE_KEY = "GPL";

// Custom upload adapter para uso com nosso serviço
class CustomUploadAdapter {
	constructor(loader) {
		this.loader = loader;
	}

	upload() {
		return this.loader.file.then(
			(file) =>
				new Promise((resolve, reject) => {
					const formData = new FormData();
					formData.append("file", file);

					uploadFileService
						.create(formData)
						.then((response) => {
							if (response && response.link) {
								resolve({
									default: response.link,
								});
							} else {
								reject(
									"Não foi possível obter o link da imagem",
								);
							}
						})
						.catch((error) => {
							console.error("Erro no upload:", error);
							reject(error);
						});
				}),
		);
	}

	abort() {
		// Implemente se necessário
	}
}

// Plugin para integrar o custom adapter
function CustomUploadAdapterPlugin(editor) {
	editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
		return new CustomUploadAdapter(loader);
	};
}

export default function CkEditor({ data, onChange }) {
	const editorContainerRef = useRef(null);
	const editorRef = useRef(null);
	const [isLayoutReady, setIsLayoutReady] = useState(false);

	useEffect(() => {
		setIsLayoutReady(true);
		return () => setIsLayoutReady(false);
	}, []);

	const { editorConfig } = useMemo(() => {
		if (!isLayoutReady) {
			return {};
		}

		return {
			editorConfig: {
				toolbar: {
					items: [
						"undo",
						"redo",
						"|",
						"sourceEditing",
						"showBlocks",
						// "findAndReplace",
						"fullscreen",
						"|",
						"heading",
						// "style",
						"|",
						"fontSize",
						"fontFamily",
						"fontColor",
						"fontBackgroundColor",
						"|",
						"bold",
						"italic",
						"underline",
						"strikethrough",
						// "subscript",
						// "superscript",
						// "code",
						"removeFormat",
						"|",
						// "emoji",
						// "specialCharacters",
						"horizontalLine",
						"link",
						"bookmark",
						"insertImage",
						// "insertImageViaUrl",
						// "mediaEmbed",
						"insertTable",
						// "highlight",
						"blockQuote",
						// "codeBlock",
						"htmlEmbed",
						"|",
						"alignment",
						"|",
						"bulletedList",
						"numberedList",
						"todoList",
						"outdent",
						"indent",
					],
					shouldNotGroupWhenFull: true,
				},
				extraPlugins: [CustomUploadAdapterPlugin],
				plugins: [
					Alignment,
					Autoformat,
					AutoImage,
					AutoLink,
					Autosave,
					BlockQuote,
					Bold,
					Bookmark,
					Code,
					CodeBlock,
					Emoji,
					Essentials,
					FindAndReplace,
					FontBackgroundColor,
					FontColor,
					FontFamily,
					FontSize,
					FullPage,
					Fullscreen,
					GeneralHtmlSupport,
					Heading,
					Highlight,
					HorizontalLine,
					HtmlComment,
					HtmlEmbed,
					ImageBlock,
					ImageCaption,
					ImageInline,
					ImageInsert,
					ImageInsertViaUrl,
					ImageResize,
					ImageStyle,
					ImageTextAlternative,
					ImageToolbar,
					ImageUpload,
					Indent,
					IndentBlock,
					Italic,
					Link,
					LinkImage,
					List,
					ListProperties,
					MediaEmbed,
					Mention,
					Paragraph,
					PasteFromOffice,
					PlainTableOutput,
					RemoveFormat,
					ShowBlocks,
					SimpleUploadAdapter,
					SourceEditing,
					SpecialCharacters,
					SpecialCharactersArrows,
					SpecialCharactersCurrency,
					SpecialCharactersEssentials,
					SpecialCharactersLatin,
					SpecialCharactersMathematical,
					SpecialCharactersText,
					Strikethrough,
					Style,
					Subscript,
					Superscript,
					Table,
					TableCaption,
					TableCellProperties,
					TableColumnResize,
					TableProperties,
					TableToolbar,
					TextTransformation,
					TodoList,
					Underline,
				],
				fontFamily: {
					supportAllValues: true,
				},
				fontSize: {
					options: [10, 12, 14, "default", 18, 20, 22],
					supportAllValues: true,
				},
				fullscreen: {
					onEnterCallback: (container) =>
						container.classList.add(
							"editor-container",
							"editor-container_classic-editor",
							"editor-container_include-style",
							"editor-container_include-fullscreen",
							"main-container",
						),
				},
				heading: {
					options: [
						{
							model: "paragraph",
							title: "Paragraph",
							class: "ck-heading_paragraph",
						},
						{
							model: "heading1",
							view: "h1",
							title: "Heading 1",
							class: "ck-heading_heading1",
						},
						{
							model: "heading2",
							view: "h2",
							title: "Heading 2",
							class: "ck-heading_heading2",
						},
						{
							model: "heading3",
							view: "h3",
							title: "Heading 3",
							class: "ck-heading_heading3",
						},
						{
							model: "heading4",
							view: "h4",
							title: "Heading 4",
							class: "ck-heading_heading4",
						},
						{
							model: "heading5",
							view: "h5",
							title: "Heading 5",
							class: "ck-heading_heading5",
						},
						{
							model: "heading6",
							view: "h6",
							title: "Heading 6",
							class: "ck-heading_heading6",
						},
					],
				},
				htmlSupport: {
					allow: [
						{
							name: /^.*$/,
							styles: true,
							attributes: true,
							classes: true,
						},
					],
				},
				image: {
					toolbar: [
						"toggleImageCaption",
						"imageTextAlternative",
						"|",
						"imageStyle:inline",
						"imageStyle:wrapText",
						"imageStyle:breakText",
						"|",
						"resizeImage",
					],
					upload: {
						types: ["jpeg", "png", "gif", "bmp", "webp", "jpg"],
					},
					insert: {
						type: "auto",
					},
				},
				licenseKey: LICENSE_KEY,
				link: {
					addTargetToExternalLinks: true,
					defaultProtocol: "https://",
					decorators: {
						toggleDownloadable: {
							mode: "manual",
							label: "Downloadable",
							attributes: {
								download: "file",
							},
						},
					},
				},
				list: {
					properties: {
						styles: true,
						startIndex: true,
						reversed: true,
					},
				},
				mention: {
					feeds: [
						{
							marker: "@",
							feed: [
								/* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
							],
						},
					],
				},
				placeholder: "Type or paste your content here!",
				style: {
					definitions: [
						{
							name: "Article category",
							element: "h3",
							classes: ["category"],
						},
						{
							name: "Title",
							element: "h2",
							classes: ["document-title"],
						},
						{
							name: "Subtitle",
							element: "h3",
							classes: ["document-subtitle"],
						},
						{
							name: "Info box",
							element: "p",
							classes: ["info-box"],
						},
						{
							name: "CTA Link Primary",
							element: "a",
							classes: ["button", "button--green"],
						},
						{
							name: "CTA Link Secondary",
							element: "a",
							classes: ["button", "button--black"],
						},
						{
							name: "Marker",
							element: "span",
							classes: ["marker"],
						},
						{
							name: "Spoiler",
							element: "span",
							classes: ["spoiler"],
						},
					],
				},
				table: {
					contentToolbar: [
						"tableColumn",
						"tableRow",
						"mergeTableCells",
						"tableProperties",
						"tableCellProperties",
					],
				},
				preserveWhitespace: true,
				clipboard: {
					handleFiles: true,
					preserveWhitespace: true,
				},
			},
		};
	}, [isLayoutReady]);

	return (
		<div className="main-container">
			<div
				className="editor-container editor-container_classic-editor editor-container_include-style editor-container_include-fullscreen"
				ref={editorContainerRef}
			>
				<div className="editor-container__editor">
					<div ref={editorRef}>
						{editorConfig && (
							<CKEditor
								editor={ClassicEditor}
								data={data}
								onChange={onChange}
								config={editorConfig}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
